import { UserType } from "@models/auth";
import { BooleanKeys } from "@models/core";
import { ManagedAccountFeeAnalysisContainerDTO } from "@models/managed-accounts/entities/step/fee";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";
import { AnalysisSummarySectionId } from "@models/platform-analysis/enums/summary/sectionId";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { FeesDisplayStyle } from "../../enums/fee/displayStyle";
import { FeeHeatmapMode } from "../../enums/fee/heatmapMode";
import { FeeMissingInvestmentsOption } from "../../enums/fee/missingInvestments";
import {
  AnalysisDTO,
  AnalysisUserType,
  HiddenSubProductDTO,
} from "../analysis";
import { AnalysisCommentDTO } from "../comment";
import { DisclaimerDTO } from "../disclaimer";
import { FeePortfolioDetailDTO } from "../portfolioDetails";
import { SelectedProductDTO } from "../selectedProduct";
import { AnalysisStepDTO } from "../step";
import { AnalysisStepBusinessMetricDTO } from "./businessMetric";
import {
  AnalysisFeatureDTO,
  AnalysisFeatureIncludeDTO,
  AnalysisSectionDTO,
  FeatureAnalysisRowDTO,
} from "./feature";
import { AnalysisFeeDTO, FeeSubProductDTO } from "./fee";

export class AnalysisSummaryDTO {
  name = "";
  ownerId = "";
  ownerName = "";
  ownerType: UserType = UserType.SupplierPremiumOwner;
  ownerAvatarUrl: string | null = null;
  ownerARN: string | null = null;
  supplierId: string | null = null;
  supplierName: string | null = null;
  adviserName: string | null = null;
  practiceName: string | null = null;
  objectives: string | null = null;
  serial: string | null = null;
  concludingRemarks: string | null = null;
  firmId: string | null = null;
  firmAFSL: string | null = null;
  firmLogo: string | null = null;
  firmName: string | null = null;

  hasFeatureStep = false;
  hasBusinessMetricStep = false;
  hasFeeStep = false;
  lastModifiedDate: Date | null = null;
  featureAnalysis: AnalysisFeatureDTO | null = null;
  businessMetricSelectedFeatures: AnalysisFeatureIncludeDTO[] | null = null;
  businessMetricAnalysis: AnalysisStepBusinessMetricDTO | null = null;
  feeAnalysis: AnalysisFeeDTO | null = null;
  feePortfolioDetails: FeePortfolioDetailDTO | null = null;
  feesDisplayStyle: FeesDisplayStyle = FeesDisplayStyle.Dollar;
  feeMissingInvestmentsStyle: FeeMissingInvestmentsOption =
    FeeMissingInvestmentsOption.AllPlatform;
  investmentMenu: FeeInvestmentMenuOption = FeeInvestmentMenuOption.AllPlatform;
  feeTableViewMode: FeeHeatmapMode = FeeHeatmapMode.Projection;
  featureAnalysisTableViewMode: TableViewMode = TableViewMode.Default;
  analysisComments: AnalysisCommentDTO[] | null = null;
  selectedProducts: SelectedProductDTO[] | null = null;
  hiddenSubProducts: HiddenSubProductDTO[] | null = null;
  familyGroupName: string | null = null;
  familyMembersNames: string[] | null = null;
  selectedFeatures: AnalysisFeatureIncludeDTO[] | null = null;
  showSubSection = true;
  showComments = true;
  showSelectedFeatures = true;
  showAnalysisScore = true;
  hideFeeAnalysisGraph = false;
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  smaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  disqualifiedSmaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  smaTableViewMode?: FeesDisplayMode;
  excludePerformanceInSMATable?: boolean;
  hidePortfolioCompositionSubtotals = false;
  showAppendixFeatureAnalysis: boolean | null = null;
  showAppendixBusinessMetricAnalysis: boolean | null = null;
  showAppendixFeeAnalysis: boolean | null = null;
  appendixFeeMissingInvestmentsStyle: FeeMissingInvestmentsOption | null = null;
  appendixInvestmentMenu: FeeInvestmentMenuOption | null = null;
  portfolioCompositionByPlatform?: PortfolioCompositionDTO;
  productRecommendations?: ProductRecommendationDTO[];
  disqualifiedPortfolioCompositionByPlatform?: PortfolioCompositionDTO;
  specificCommentSetting?: SummaryCommentSettingDto;
}

export class SummaryCommentSettingDto {
  showFeature = false;
  showBusinessMetric = false;
  showFee = false;
  showSummary = false;
}

export class PortfolioCompositionDTO {
  portfolioSize = 0;
  header = "";
  setups: PortfolioCompositionSetupDTO[] = [];
  sections: PortfolioCompositionSectionDTO[] = [];
  platformCodes: PortfolioCompositionPlatformCodeDTO[] = [];
}

export class PortfolioCompositionSetupDTO {
  id = "";
  name = "";
  setupType = "";
  isLatest?: boolean = false;
  outdatedAt: Date | null = null;
  productName = "";
  logo = "";
  isProductMain?: boolean = false;
  order = 0;
  rateCardVersion?: number = 0;

  // internal use only
  productId?: string = "";
  recommendation?: ProductRecommendationDTO;
}

export class PortfolioCompositionSectionDTO {
  isFeeCalculatorSection?: boolean = false;
  order = 0;
  rows: PortfolioCompositionRowDTO[] = [];
}

export class PortfolioCompositionRowDTO {
  id = "";
  name = "";
  isFeeCalculator?: boolean = false;
  isSubTotal?: boolean = false;
  isSingleRow?: boolean = false;
  order = 0;
  cells: PortfolioCompositionCellDTO[] = [];
}

export class PortfolioCompositionCellDTO {
  setupId = "";
  value = "";
  valueInPercent = "";
  order = 0;
  belowMinimumInvestmentSize?: boolean = false;
  rateCardVersion?: number = 0;

  // internal use only
  productId?: string = "";
}

export class PortfolioCompositionPlatformCodeDTO {
  shCode = "";
  name = "";
  order = 0;
  cells: PortfolioCompositionPlatformCodeCellDTO[] = [];
}

export class PortfolioCompositionPlatformCodeCellDTO {
  id = "";
  platformCode = "";
  order = 0;
  rateCardVersion?: number | null = null;
  productId?: string = "";
  setupType?: AnalysisFeePortfolioSetupType | null = null;
}

export class ProductRecommendationDTO {
  productId = "";
  productName = "";
  productLogo = "";
  order = 0;
  isProductMain = false;
  recommendationType: ProductRecommendationTypes | null = null;
  additionalCustomLabel: string | null = null;
}

export interface RenderAnalysisSummarySectionProps {
  id: AnalysisSummarySectionId;
  name?: { [key in AnalysisUserType]?: string };
  showFor: AnalysisUserType[];
  Component: React.MemoExoticComponent<
    (props: RenderAnalysisSummaryItemProps) => JSX.Element
  > | null;
  dependencyField?: BooleanKeys<AnalysisSummaryDTO>;
  disabled?: boolean;
  pdfPageHandler?: ((props: PDFPageHandlerProps) => number) | null;
  pdfTitle?: { [key in AnalysisUserType]?: string };
  isAppendix?: boolean;
}

export interface RenderAnalysisSummaryItemProps
  extends Omit<RenderAnalysisSummarySectionProps, "Component"> {
  userType: AnalysisUserType;
  isReview: boolean;
  portfolioLevel?: FeeCalculatorPortfolioLevel;
  isReadOnly?: boolean;
}

export interface RenderPDFItemProps {
  analysis: AnalysisDTO;
}

export interface PDFPageHandlerProps {
  pages: PDFPageTemplate[];
  spaceAvailable: number;
  analysis: AnalysisDTO;
  title?: string;
  analysisSteps?: AnalysisStepDTO;
  isAppendix?: boolean;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  hiddenIdsIndexMap?: boolean[];
  disqualifiedHiddenIdsIndexMap?: boolean[];
  hiddenIds?: string[];
  disqualifiedHiddenIds?: string[];
  feeSubProducts?: FeeSubProductDTO[];
  disqualifiedFeeSubProducts?: FeeSubProductDTO[];
  featureAnalysisTableRows?: FeatureAnalysisRowDTO[];
  businessMetricTableRows?: FeatureAnalysisRowDTO[];
  disclaimers?: DisclaimerDTO[];
  smaTableRows?: FeatureAnalysisRowDTO[];
  disqualifiedSmaTableRows?: FeatureAnalysisRowDTO[];
}

export interface PDFPageTemplate {
  components: JSX.Element[];
  isAppendix?: boolean;
}

export interface PDFHtmlElement {
  content: string | null;
  height: number | null;
}

export interface PDFCommentDetail {
  elements?: PDFHtmlElement[];
  totalHeight: number;
}

export interface PDFAnalysisSectionDTO extends AnalysisSectionDTO {
  isEssential?: boolean;
  type?: "Section" | "SubSection" | "Feature";
}

export interface PDFAnalysisComment extends AnalysisCommentDTO {
  height: number;
  isContinued?: boolean;
}

export interface PDFReviewCommentSeparated {
  featureDetail?: PDFCommentDetail;
  businessMetricDetail?: PDFCommentDetail;
  feeDetail?: PDFCommentDetail;
  summaryDetail?: PDFCommentDetail;
}
